import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, Heart, Sparkles, Truck, ShieldCheck } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const ZelligePattern = () => (
  <svg className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="zellige-footer" x="0" y="0" width="64" height="64" patternUnits="userSpaceOnUse">
        <path d="M32 0L64 32L32 64L0 32Z" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <circle cx="32" cy="32" r="5" fill="none" stroke="currentColor" strokeWidth="0.3" />
        <circle cx="32" cy="32" r="1.2" fill="currentColor" opacity="0.6" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#zellige-footer)" />
  </svg>
);

const Ornament = () => (
  <div className="flex items-center justify-center gap-3 my-2" aria-hidden>
    <span className="h-px w-10 bg-gradient-to-r from-transparent to-[hsl(var(--gold)/0.5)]" />
    <Sparkles className="w-3 h-3 text-[hsl(var(--gold))]" />
    <span className="h-px w-10 bg-gradient-to-l from-transparent to-[hsl(var(--gold)/0.5)]" />
  </div>
);

export const Footer = () => {
  const year = new Date().getFullYear();
  const { lang, t } = useLang();

  const tagline = t({
    fr: "Adhésifs nés de la nostalgie d'un pays et de la reconnaissance envers un autre. Chaque création est un pont entre la Tunisie et le Canada.",
    en: "Stickers born from longing for one country and gratitude toward another. Each piece is a bridge between Tunisia and Canada.",
  });

  const navLinks = lang === "en"
    ? [
        { label: "Home", path: "/en" },
        { label: "Our Story", path: "/en/our-story" },
        { label: "Install the app", path: "/en/install" },
        { label: "Contact", path: "/en/contact" },
      ]
    : [
        { label: "Accueil", path: "/" },
        { label: "Notre Histoire", path: "/notre-histoire" },
        { label: "Installer l'app", path: "/install" },
        { label: "Contact", path: "/contact" },
      ];

  const infoLinks = lang === "en"
    ? [
        { label: "FAQ", path: "/en#faq" },
        { label: "Shipping", path: "/en/contact" },
        { label: "How to apply", path: "/en/install" },
        { label: "Privacy", path: "/en/contact" },
      ]
    : [
        { label: "FAQ", path: "/#faq" },
        { label: "Livraison", path: "/contact" },
        { label: "Comment poser", path: "/install" },
        { label: "Confidentialité", path: "/contact" },
      ];

  return (
    <footer className="relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-40 left-0 right-0 h-40 bg-gradient-to-b from-transparent via-[hsl(var(--gold)/0.18)] to-[hsl(var(--terracotta-glow)/0.28)] blur-2xl" />
      <div aria-hidden className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[80%] h-24 bg-[hsl(var(--gold)/0.25)] rounded-full blur-3xl" />

      <div className="relative h-1 bg-gradient-to-r from-[hsl(var(--terracotta))] via-[hsl(var(--gold))] to-[hsl(var(--terracotta))] shadow-[0_0_24px_hsl(var(--gold)/0.5)]" />

      <div className="relative bg-gradient-to-b from-[hsl(var(--foreground))] via-[hsl(var(--foreground))] to-[hsl(var(--foreground)/0.96)]">
        <ZelligePattern />
        <div className="absolute top-0 left-1/4 w-80 h-80 bg-[hsl(var(--terracotta)/0.10)] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[hsl(var(--gold)/0.08)] rounded-full blur-3xl" />

        <div className="container mx-auto px-4 pt-16 pb-10 relative z-10">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 pb-10 mb-12 border-b border-white/10 text-white/60">
            <div className="flex items-center gap-2 text-xs md:text-sm font-body">
              <Truck className="w-4 h-4 text-[hsl(var(--gold))]" />
              <span>{t({ fr: "Livraison gratuite dès 69 $", en: "Free shipping over $69" })}</span>
            </div>
            <span className="hidden md:inline w-1 h-1 rounded-full bg-white/20" />
            <div className="flex items-center gap-2 text-xs md:text-sm font-body">
              <ShieldCheck className="w-4 h-4 text-[hsl(var(--gold))]" />
              <span>{t({ fr: "Ventes finales · Vinyle premium", en: "Final sales · Premium vinyl" })}</span>
            </div>
            <span className="hidden md:inline w-1 h-1 rounded-full bg-white/20" />
            <div className="flex items-center gap-2 text-xs md:text-sm font-body">
              <Heart className="w-4 h-4 text-[hsl(var(--terracotta-glow))]" />
              <span>{t({ fr: "Conçu avec amour au Canada", en: "Designed with love in Canada" })}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
            <div className="col-span-2 md:col-span-2">
              <h3
                className="font-display font-bold text-3xl mb-4 tracking-tight"
                style={{
                  backgroundImage: "linear-gradient(135deg, hsl(18 78% 55%), hsl(42 72% 60%))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                EL FAZA
              </h3>
              <p className="text-sm text-white/55 font-body leading-relaxed mb-5 max-w-sm">
                {tagline}
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/50">
                <span className="text-xs font-body">{t({ fr: "De la Tunisie au Canada", en: "From Tunisia to Canada" })}</span>
                <span>🇹🇳</span>
                <Heart className="w-3 h-3 text-[hsl(var(--terracotta-glow))] fill-[hsl(var(--terracotta-glow))]" />
                <span>🇨🇦</span>
              </div>
            </div>

            <div>
              <h4 className="font-display font-semibold text-xs uppercase tracking-[0.22em] mb-5 text-[hsl(var(--gold))]">
                {t({ fr: "Navigation", en: "Navigation" })}
              </h4>
              <div className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="group inline-flex items-center gap-2 text-sm text-white/55 font-body hover:text-[hsl(var(--terracotta-glow))] transition-colors duration-300"
                  >
                    <span className="w-0 h-px bg-[hsl(var(--terracotta-glow))] group-hover:w-3 transition-all duration-300" />
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-display font-semibold text-xs uppercase tracking-[0.22em] mb-5 text-[hsl(var(--gold))]">
                {t({ fr: "Infos utiles", en: "Helpful info" })}
              </h4>
              <div className="flex flex-col gap-3">
                {infoLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.path}
                    className="group inline-flex items-center gap-2 text-sm text-white/55 font-body hover:text-[hsl(var(--terracotta-glow))] transition-colors duration-300"
                  >
                    <span className="w-0 h-px bg-[hsl(var(--terracotta-glow))] group-hover:w-3 transition-all duration-300" />
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-display font-semibold text-xs uppercase tracking-[0.22em] mb-5 text-[hsl(var(--gold))]">
                {t({ fr: "Rester en lien", en: "Stay in touch" })}
              </h4>
              <div className="space-y-3 mb-5">
                <a
                  href="mailto:elfaza.contact@gmail.com"
                  className="flex items-center gap-2 text-sm text-white/55 font-body hover:text-[hsl(var(--terracotta-glow))] transition-colors duration-300 break-all"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  elfaza.contact@gmail.com
                </a>
                <div className="flex items-center gap-2 text-sm text-white/45 font-body">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  Canada
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.instagram.com/elfaza_vibes"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram EL FAZA"
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-gradient-to-br hover:from-[hsl(var(--terracotta))] hover:to-[hsl(var(--terracotta-glow))] hover:text-white hover:border-transparent hover:shadow-[var(--shadow-warm)] hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.tiktok.com/@elfaza_vibes"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok EL FAZA"
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-white hover:text-foreground hover:border-transparent hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.51a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.05a8.26 8.26 0 0 0 4.76 1.51V7.12a4.82 4.82 0 0 1-1-.43z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <Ornament />

          <div className="mt-6 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40 font-body order-2 md:order-1">
              © {year} EL FAZA Canada. {t({ fr: "Tous droits réservés.", en: "All rights reserved." })}
            </p>
            <div className="flex items-center gap-2 order-1 md:order-2" aria-hidden>
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: i % 2 === 0 ? "hsl(42 72% 55%)" : "hsl(18 78% 50%)",
                    opacity: 0.45,
                  }}
                />
              ))}
            </div>
            <p className="text-xs text-white/30 font-body order-3">
              <Link to="/admin/login" className="text-white/25 hover:text-white/50 transition-colors">
                {t({ fr: "Espace admin", en: "Admin area" })}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
